import React, { useState } from "react";
import {
  FaBug,
  FaChevronDown,
  FaChevronUp,
  FaRocket,
} from "react-icons/fa";

import {
  formatDateLongFR,
  getTagMeta,
  getUpdateDate,
  sanitizeHtml,
  stripHtml,
} from "../utils/updatesDashboardUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-36 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaBug />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucune mise à jour trouvée
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Modifiez vos filtres ou réessayez plus tard.
        </p>
      </div>
    </section>
  );
}

function UpdateIcon({ tag }) {
  const meta = getTagMeta(tag);

  return (
    <span
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ${meta.dot}`}
    >
      {meta.value === "Bug" ? <FaBug /> : <FaRocket />}
    </span>
  );
}

export default function UpdatesFeed({ updates, loading }) {
  const [expandedIds, setExpandedIds] = useState({});

  if (loading) return <SkeletonRows />;
  if (!updates.length) return <EmptyState />;

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Historique
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Journal des nouveautés
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Présentation sous forme de fil chronologique pour rendre les notes
            de version plus lisibles qu’un simple tableau.
          </p>
        </div>

        <div className="relative space-y-4">
          <div className="absolute left-6 top-2 hidden h-[calc(100%-1rem)] w-px bg-slate-200 dark:bg-slate-800 sm:block" />

          {updates.map((update) => {
            const tag = getTagMeta(update.tag_update);
            const rawText = stripHtml(update.content_update || "");
            const isLong = rawText.length > 420;
            const isExpanded = !!expandedIds[update.id];

            return (
              <article
                key={update.id}
                className={`relative overflow-hidden rounded-[26px] border shadow-sm transition ${tag.card}`}
              >
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    <div className="relative z-10 hidden sm:block">
                      <UpdateIcon tag={update.tag_update} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${tag.badge}`}
                            >
                              {tag.label}
                            </span>

                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                              {formatDateLongFR(getUpdateDate(update))}
                            </span>
                          </div>

                          <h3 className="mt-2 text-lg font-black text-slate-900 dark:text-white">
                            {update.title_update || "Mise à jour"}
                          </h3>
                        </div>
                      </div>

                      <div
                        className={`mt-4 overflow-hidden rounded-2xl border border-white/70 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-950/70 ${
                          !isExpanded && isLong ? "max-h-56" : ""
                        }`}
                      >
                        <div
                          className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(update.content_update || ""),
                          }}
                        />
                      </div>

                      {isLong ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(update.id)}
                          className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                          {isExpanded ? "Réduire" : "Lire la suite"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}